import React, { useEffect } from 'react';

const CQRSEventSourcing = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'CQRS & Event Sourcing - Scalable Architecture Patterns 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master CQRS and Event Sourcing patterns for scalable systems. Learn implementation strategies, benefits, and real-world applications.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master CQRS and Event Sourcing patterns for scalable systems. Learn implementation strategies, benefits, and real-world applications.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>CQRS & Event Sourcing: Scalable Architecture Patterns</h1>
        <p style={styles.subtitle}>
          Separate read and write models for optimal performance and scalability
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to CQRS and Event Sourcing</h2>
          <p style={styles.paragraph}>
            CQRS (Command Query Responsibility Segregation) and Event Sourcing are architectural patterns 
            that work together to create highly scalable, maintainable, and auditable systems. CQRS 
            separates read and write operations into different models, while Event Sourcing stores the 
            state of an application as a sequence of events rather than just the current state.
          </p>
          <p style={styles.paragraph}>
            These patterns are particularly valuable in complex domains where business logic is intricate, 
            audit trails are crucial, and different read and write performance characteristics are needed. 
            They enable systems to scale reads and writes independently and provide complete historical 
            context for all changes.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>CQRS (Command Query Responsibility Segregation)</h2>
          
          <h3 style={styles.heading}>Core Concepts</h3>
          <p style={styles.paragraph}>
            CQRS separates the responsibility of handling commands (writes) from queries (reads), 
            allowing each side to be optimized independently for their specific use cases.
          </p>
          <pre style={styles.codeBlock}>
{`CQRS Architecture Overview:

Traditional Architecture:
┌─────────────┐    ┌─────────────┐    ┌─────────────┐
│   Client    │───▶│  Service    │───▶│  Database   │
└─────────────┘    │             │    │             │
                   │ Read/Write  │    │ Single DB   │
                   └─────────────┘    └─────────────┘

CQRS Architecture:
┌─────────────┐    ┌─────────────┐    ┌─────────────┐
│   Client    │───▶│  Command    │───▶│  Write DB   │
│             │    │  Handler    │    │             │
└─────────────┘    └─────────────┘    └─────────────┘
       │
       └──────────▶┌─────────────┐    ┌─────────────┐
                   │   Query     │───▶│   Read DB   │
                   │  Handler    │    │             │
                   └─────────────┘    └─────────────┘

Key Benefits:
✅ Independent scaling of reads and writes
✅ Optimized data models for each operation
✅ Better performance for complex queries
✅ Simplified business logic separation
✅ Technology flexibility (different databases)

Trade-offs:
❌ Increased complexity
❌ Eventual consistency between read/write models
❌ More infrastructure to manage
❌ Data synchronization challenges

When to Use CQRS:
- Complex business domains
- Different read/write performance requirements
- Need for audit trails and historical data
- High read-to-write ratios
- Multiple read models needed
- Collaborative systems with conflicts

When NOT to Use CQRS:
- Simple CRUD applications
- Strong consistency requirements
- Small teams with limited complexity
- Tight coupling between reads and writes needed`}
          </pre>

          <h3 style={styles.heading}>CQRS Implementation</h3>
          <p style={styles.paragraph}>
            Implementing CQRS requires careful design of command and query handlers, along with 
            synchronization mechanisms between write and read models.
          </p>
          <pre style={styles.codeBlock}>
{`CQRS Implementation Example:

# Python implementation with FastAPI
from fastapi import FastAPI, HTTPException, Depends
from pydantic import BaseModel
from typing import List, Optional, Dict, Any
from abc import ABC, abstractmethod
import uuid
import time
from dataclasses import dataclass
from enum import Enum

# Domain Models
class OrderStatus(Enum):
    PENDING = "pending"
    CONFIRMED = "confirmed"
    SHIPPED = "shipped"
    DELIVERED = "delivered"
    CANCELLED = "cancelled"

@dataclass
class OrderItem:
    product_id: str
    quantity: int
    price: float

# Commands (Write Side)
class Command(ABC):
    pass

class CreateOrderCommand(Command):
    def __init__(self, customer_id: str, items: List[OrderItem]):
        self.command_id = str(uuid.uuid4())
        self.customer_id = customer_id
        self.items = items
        self.timestamp = time.time()

class UpdateOrderStatusCommand(Command):
    def __init__(self, order_id: str, status: OrderStatus):
        self.command_id = str(uuid.uuid4())
        self.order_id = order_id
        self.status = status
        self.timestamp = time.time()

class CancelOrderCommand(Command):
    def __init__(self, order_id: str, reason: str):
        self.command_id = str(uuid.uuid4())
        self.order_id = order_id
        self.reason = reason
        self.timestamp = time.time()

# Events
class Event(ABC):
    def __init__(self):
        self.event_id = str(uuid.uuid4())
        self.timestamp = time.time()

class OrderCreatedEvent(Event):
    def __init__(self, order_id: str, customer_id: str, items: List[OrderItem], total: float):
        super().__init__()
        self.order_id = order_id
        self.customer_id = customer_id
        self.items = items
        self.total = total

class OrderStatusUpdatedEvent(Event):
    def __init__(self, order_id: str, old_status: OrderStatus, new_status: OrderStatus):
        super().__init__()
        self.order_id = order_id
        self.old_status = old_status
        self.new_status = new_status

class OrderCancelledEvent(Event):
    def __init__(self, order_id: str, reason: str):
        super().__init__()
        self.order_id = order_id
        self.reason = reason

# Write Model (Aggregate)
class Order:
    def __init__(self, order_id: str = None):
        self.order_id = order_id or str(uuid.uuid4())
        self.customer_id: Optional[str] = None
        self.items: List[OrderItem] = []
        self.status = OrderStatus.PENDING
        self.total = 0.0
        self.created_at: Optional[float] = None
        self.updated_at: Optional[float] = None
        
        # Event sourcing
        self.version = 0
        self.uncommitted_events: List[Event] = []
    
    def create_order(self, customer_id: str, items: List[OrderItem]) -> List[Event]:
        """Create new order"""
        if self.customer_id is not None:
            raise ValueError("Order already exists")
        
        total = sum(item.quantity * item.price for item in items)
        
        event = OrderCreatedEvent(self.order_id, customer_id, items, total)
        self._apply_event(event)
        
        return [event]
    
    def update_status(self, new_status: OrderStatus) -> List[Event]:
        """Update order status"""
        if self.customer_id is None:
            raise ValueError("Order does not exist")
        
        if self.status == new_status:
            return []
        
        old_status = self.status
        event = OrderStatusUpdatedEvent(self.order_id, old_status, new_status)
        self._apply_event(event)
        
        return [event]
    
    def cancel_order(self, reason: str) -> List[Event]:
        """Cancel order"""
        if self.customer_id is None:
            raise ValueError("Order does not exist")
        
        if self.status == OrderStatus.CANCELLED:
            return []
        
        event = OrderCancelledEvent(self.order_id, reason)
        self._apply_event(event)
        
        return [event]
    
    def _apply_event(self, event: Event):
        """Apply event to aggregate state"""
        if isinstance(event, OrderCreatedEvent):
            self.customer_id = event.customer_id
            self.items = event.items
            self.total = event.total
            self.status = OrderStatus.PENDING
            self.created_at = event.timestamp
            self.updated_at = event.timestamp
        
        elif isinstance(event, OrderStatusUpdatedEvent):
            self.status = event.new_status
            self.updated_at = event.timestamp
        
        elif isinstance(event, OrderCancelledEvent):
            self.status = OrderStatus.CANCELLED
            self.updated_at = event.timestamp
        
        self.version += 1
        self.uncommitted_events.append(event)
    
    def mark_events_as_committed(self):
        """Mark events as committed after saving"""
        self.uncommitted_events.clear()

# Command Handlers (Write Side)
class CommandHandler(ABC):
    @abstractmethod
    def handle(self, command: Command) -> List[Event]:
        pass

class OrderCommandHandler(CommandHandler):
    def __init__(self, order_repository, event_store):
        self.order_repository = order_repository
        self.event_store = event_store
    
    def handle(self, command: Command) -> List[Event]:
        if isinstance(command, CreateOrderCommand):
            return self._handle_create_order(command)
        elif isinstance(command, UpdateOrderStatusCommand):
            return self._handle_update_status(command)
        elif isinstance(command, CancelOrderCommand):
            return self._handle_cancel_order(command)
        else:
            raise ValueError(f"Unknown command type: {type(command)}")
    
    def _handle_create_order(self, command: CreateOrderCommand) -> List[Event]:
        order = Order()
        events = order.create_order(command.customer_id, command.items)
        
        # Save to event store
        for event in events:
            self.event_store.save_event(order.order_id, event, order.version)
        
        # Save aggregate
        self.order_repository.save(order)
        order.mark_events_as_committed()
        
        return events
    
    def _handle_update_status(self, command: UpdateOrderStatusCommand) -> List[Event]:
        order = self.order_repository.get_by_id(command.order_id)
        if not order:
            raise ValueError(f"Order {command.order_id} not found")
        
        events = order.update_status(command.status)
        
        # Save events and aggregate
        for event in events:
            self.event_store.save_event(order.order_id, event, order.version)
        
        self.order_repository.save(order)
        order.mark_events_as_committed()
        
        return events
    
    def _handle_cancel_order(self, command: CancelOrderCommand) -> List[Event]:
        order = self.order_repository.get_by_id(command.order_id)
        if not order:
            raise ValueError(f"Order {command.order_id} not found")
        
        events = order.cancel_order(command.reason)
        
        # Save events and aggregate
        for event in events:
            self.event_store.save_event(order.order_id, event, order.version)
        
        self.order_repository.save(order)
        order.mark_events_as_committed()
        
        return events

# Read Models (Query Side)
class OrderReadModel:
    def __init__(self, order_id: str, customer_id: str, status: str, 
                 total: float, created_at: float, updated_at: float):
        self.order_id = order_id
        self.customer_id = customer_id
        self.status = status
        self.total = total
        self.created_at = created_at
        self.updated_at = updated_at

class CustomerOrderSummary:
    def __init__(self, customer_id: str, total_orders: int, 
                 total_spent: float, last_order_date: float):
        self.customer_id = customer_id
        self.total_orders = total_orders
        self.total_spent = total_spent
        self.last_order_date = last_order_date

# Query Handlers (Read Side)
class QueryHandler(ABC):
    @abstractmethod
    def handle(self, query) -> Any:
        pass

class OrderQueryHandler(QueryHandler):
    def __init__(self, read_model_store):
        self.read_model_store = read_model_store
    
    def get_order_by_id(self, order_id: str) -> Optional[OrderReadModel]:
        return self.read_model_store.get_order(order_id)
    
    def get_orders_by_customer(self, customer_id: str) -> List[OrderReadModel]:
        return self.read_model_store.get_orders_by_customer(customer_id)
    
    def get_orders_by_status(self, status: OrderStatus) -> List[OrderReadModel]:
        return self.read_model_store.get_orders_by_status(status.value)
    
    def get_customer_summary(self, customer_id: str) -> Optional[CustomerOrderSummary]:
        return self.read_model_store.get_customer_summary(customer_id)

# Event Handlers (Projections)
class EventHandler(ABC):
    @abstractmethod
    def handle(self, event: Event):
        pass

class OrderProjectionHandler(EventHandler):
    def __init__(self, read_model_store):
        self.read_model_store = read_model_store
    
    def handle(self, event: Event):
        if isinstance(event, OrderCreatedEvent):
            self._handle_order_created(event)
        elif isinstance(event, OrderStatusUpdatedEvent):
            self._handle_status_updated(event)
        elif isinstance(event, OrderCancelledEvent):
            self._handle_order_cancelled(event)
    
    def _handle_order_created(self, event: OrderCreatedEvent):
        read_model = OrderReadModel(
            order_id=event.order_id,
            customer_id=event.customer_id,
            status=OrderStatus.PENDING.value,
            total=event.total,
            created_at=event.timestamp,
            updated_at=event.timestamp
        )
        self.read_model_store.save_order(read_model)
        self._update_customer_summary(event.customer_id)
    
    def _handle_status_updated(self, event: OrderStatusUpdatedEvent):
        order = self.read_model_store.get_order(event.order_id)
        if order:
            order.status = event.new_status.value
            order.updated_at = event.timestamp
            self.read_model_store.save_order(order)
    
    def _handle_order_cancelled(self, event: OrderCancelledEvent):
        order = self.read_model_store.get_order(event.order_id)
        if order:
            order.status = OrderStatus.CANCELLED.value
            order.updated_at = event.timestamp
            self.read_model_store.save_order(order)
    
    def _update_customer_summary(self, customer_id: str):
        orders = self.read_model_store.get_orders_by_customer(customer_id)
        
        total_orders = len(orders)
        total_spent = sum(order.total for order in orders)
        last_order_date = max(order.created_at for order in orders) if orders else 0
        
        summary = CustomerOrderSummary(customer_id, total_orders, total_spent, last_order_date)
        self.read_model_store.save_customer_summary(summary)

# CQRS Facade
class CQRSSystem:
    def __init__(self):
        self.command_handlers = {}
        self.query_handlers = {}
        self.event_handlers = []
    
    def register_command_handler(self, command_type: type, handler: CommandHandler):
        self.command_handlers[command_type] = handler
    
    def register_query_handler(self, query_type: type, handler: QueryHandler):
        self.query_handlers[query_type] = handler
    
    def register_event_handler(self, handler: EventHandler):
        self.event_handlers.append(handler)
    
    def execute_command(self, command: Command) -> List[Event]:
        handler = self.command_handlers.get(type(command))
        if not handler:
            raise ValueError(f"No handler for command {type(command)}")
        
        events = handler.handle(command)
        
        # Publish events to event handlers (projections)
        for event in events:
            for event_handler in self.event_handlers:
                try:
                    event_handler.handle(event)
                except Exception as e:
                    print(f"Error in event handler: {e}")
        
        return events
    
    def execute_query(self, query_handler_type: type, method_name: str, *args, **kwargs):
        handler = self.query_handlers.get(query_handler_type)
        if not handler:
            raise ValueError(f"No handler for query {query_handler_type}")
        
        method = getattr(handler, method_name)
        return method(*args, **kwargs)

# Usage Example
def setup_cqrs_system():
    # Initialize repositories and stores (simplified)
    order_repository = InMemoryOrderRepository()
    event_store = InMemoryEventStore()
    read_model_store = InMemoryReadModelStore()
    
    # Initialize handlers
    command_handler = OrderCommandHandler(order_repository, event_store)
    query_handler = OrderQueryHandler(read_model_store)
    projection_handler = OrderProjectionHandler(read_model_store)
    
    # Setup CQRS system
    cqrs = CQRSSystem()
    cqrs.register_command_handler(CreateOrderCommand, command_handler)
    cqrs.register_command_handler(UpdateOrderStatusCommand, command_handler)
    cqrs.register_command_handler(CancelOrderCommand, command_handler)
    cqrs.register_query_handler(OrderQueryHandler, query_handler)
    cqrs.register_event_handler(projection_handler)
    
    return cqrs

# Example usage
cqrs = setup_cqrs_system()

# Execute commands
items = [OrderItem("product-1", 2, 29.99), OrderItem("product-2", 1, 49.99)]
create_command = CreateOrderCommand("customer-123", items)
events = cqrs.execute_command(create_command)

order_id = events[0].order_id
print(f"Order created: {order_id}")

# Execute queries
order = cqrs.execute_query(OrderQueryHandler, 'get_order_by_id', order_id)
print(f"Order status: {order.status}")

customer_orders = cqrs.execute_query(OrderQueryHandler, 'get_orders_by_customer', "customer-123")
print(f"Customer has {len(customer_orders)} orders")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Event Sourcing</h2>
          
          <h3 style={styles.heading}>Core Concepts</h3>
          <p style={styles.paragraph}>
            Event Sourcing stores the state of an application as a sequence of events. Instead of 
            storing just the current state, every change is captured as an event that can be replayed 
            to reconstruct the current state.
          </p>
          <pre style={styles.codeBlock}>
{`Event Sourcing Concepts:

Traditional State Storage:
┌─────────────────────────────────────────────────────────────┐
│                    Current State Only                       │
├─────────────────────────────────────────────────────────────┤
│ Account ID: 123                                             │
│ Balance: $1,500                                             │
│ Status: Active                                              │
│ Last Updated: 2024-01-15                                    │
└─────────────────────────────────────────────────────────────┘

Event Sourcing Storage:
┌─────────────────────────────────────────────────────────────┐
│                     Event Stream                            │
├─────────────────────────────────────────────────────────────┤
│ 1. AccountCreated    { id: 123, initial: $0 }              │
│ 2. MoneyDeposited    { amount: $1000, balance: $1000 }     │
│ 3. MoneyWithdrawn    { amount: $200, balance: $800 }       │
│ 4. MoneyDeposited    { amount: $700, balance: $1500 }      │
│ 5. AccountActivated { status: "active" }                   │
└─────────────────────────────────────────────────────────────┘

Current State = Replay Events 1-5

Key Benefits:
✅ Complete audit trail
✅ Time travel (reconstruct state at any point)
✅ Event replay for debugging
✅ Natural integration with event-driven architecture
✅ Temporal queries (what was state at time X?)
✅ Never lose data (events are immutable)

Trade-offs:
❌ Storage overhead (all events stored)
❌ Complexity in event schema evolution
❌ Performance considerations for large event streams
❌ Eventual consistency challenges
❌ Snapshot management needed for performance

Event Store Structure:
┌─────────────┬─────────────┬─────────────┬─────────────┬─────────────┐
│ Stream ID   │ Version     │ Event Type  │ Event Data  │ Timestamp   │
├─────────────┼─────────────┼─────────────┼─────────────┼─────────────┤
│ account-123 │ 1           │ Created     │ {...}       │ 1642234800  │
│ account-123 │ 2           │ Deposited   │ {...}       │ 1642235000  │
│ account-123 │ 3           │ Withdrawn   │ {...}       │ 1642235200  │
│ order-456   │ 1           │ Created     │ {...}       │ 1642235400  │
│ order-456   │ 2           │ Confirmed   │ {...}       │ 1642235600  │
└─────────────┴─────────────┴─────────────┴─────────────┴─────────────┘`}
          </pre>

          <h3 style={styles.heading}>Event Store Implementation</h3>
          <p style={styles.paragraph}>
            An Event Store is the core component of Event Sourcing, providing append-only storage 
            for events with strong consistency guarantees and efficient replay capabilities.
          </p>
          <pre style={styles.codeBlock}>
{`Event Store Implementation:

import json
import time
import threading
from typing import List, Optional, Dict, Any, Callable
from dataclasses import dataclass, asdict
from abc import ABC, abstractmethod

@dataclass
class StoredEvent:
    stream_id: str
    event_id: str
    event_type: str
    event_data: Dict[str, Any]
    metadata: Dict[str, Any]
    version: int
    timestamp: float
    
    def to_dict(self) -> Dict[str, Any]:
        return asdict(self)
    
    @classmethod
    def from_dict(cls, data: Dict[str, Any]) -> 'StoredEvent':
        return cls(**data)

class EventStore(ABC):
    @abstractmethod
    def append_events(self, stream_id: str, events: List[Event], 
                     expected_version: int) -> None:
        pass
    
    @abstractmethod
    def get_events(self, stream_id: str, from_version: int = 0) -> List[StoredEvent]:
        pass
    
    @abstractmethod
    def get_all_events(self, from_position: int = 0) -> List[StoredEvent]:
        pass

class InMemoryEventStore(EventStore):
    def __init__(self):
        self._streams: Dict[str, List[StoredEvent]] = {}
        self._global_events: List[StoredEvent] = []
        self._lock = threading.RLock()
        self._global_position = 0
    
    def append_events(self, stream_id: str, events: List[Event], 
                     expected_version: int) -> None:
        with self._lock:
            # Get current stream version
            current_version = len(self._streams.get(stream_id, []))
            
            # Check optimistic concurrency
            if expected_version != current_version:
                raise ConcurrencyError(
                    f"Expected version {expected_version}, but stream is at version {current_version}"
                )
            
            # Initialize stream if doesn't exist
            if stream_id not in self._streams:
                self._streams[stream_id] = []
            
            # Append events
            for event in events:
                current_version += 1
                
                stored_event = StoredEvent(
                    stream_id=stream_id,
                    event_id=event.event_id,
                    event_type=type(event).__name__,
                    event_data=self._serialize_event(event),
                    metadata={
                        'correlation_id': getattr(event, 'correlation_id', None),
                        'causation_id': getattr(event, 'causation_id', None)
                    },
                    version=current_version,
                    timestamp=event.timestamp
                )
                
                self._streams[stream_id].append(stored_event)
                self._global_events.append(stored_event)
                self._global_position += 1
    
    def get_events(self, stream_id: str, from_version: int = 0) -> List[StoredEvent]:
        with self._lock:
            stream_events = self._streams.get(stream_id, [])
            return [e for e in stream_events if e.version > from_version]
    
    def get_all_events(self, from_position: int = 0) -> List[StoredEvent]:
        with self._lock:
            return self._global_events[from_position:]
    
    def _serialize_event(self, event: Event) -> Dict[str, Any]:
        """Serialize event to dictionary"""
        # Simple serialization - in production, use proper serialization
        return {
            'event_id': event.event_id,
            'timestamp': event.timestamp,
            **{k: v for k, v in event.__dict__.items() 
               if not k.startswith('_') and k not in ['event_id', 'timestamp']}
        }
    
    def create_snapshot(self, stream_id: str, aggregate_data: Dict[str, Any], 
                       version: int) -> None:
        """Create snapshot for performance optimization"""
        # Implementation for snapshots
        pass

class ConcurrencyError(Exception):
    pass

# Aggregate Base Class with Event Sourcing
class AggregateRoot:
    def __init__(self, aggregate_id: str):
        self.aggregate_id = aggregate_id
        self.version = 0
        self.uncommitted_events: List[Event] = []
    
    def apply_event(self, event: Event, is_new: bool = True):
        """Apply event to aggregate state"""
        # Find and call the appropriate apply method
        method_name = f"_apply_{type(event).__name__}"
        if hasattr(self, method_name):
            method = getattr(self, method_name)
            method(event)
        
        if is_new:
            self.uncommitted_events.append(event)
        
        self.version += 1
    
    def get_uncommitted_events(self) -> List[Event]:
        return self.uncommitted_events.copy()
    
    def mark_events_as_committed(self):
        self.uncommitted_events.clear()
    
    def load_from_history(self, events: List[StoredEvent]):
        """Reconstruct aggregate from event history"""
        for stored_event in events:
            event = self._deserialize_event(stored_event)
            self.apply_event(event, is_new=False)
    
    def _deserialize_event(self, stored_event: StoredEvent) -> Event:
        """Deserialize stored event back to domain event"""
        # Simple deserialization - in production, use proper deserialization
        event_type = stored_event.event_type
        event_data = stored_event.event_data
        
        # Map event types to classes
        event_classes = {
            'OrderCreatedEvent': OrderCreatedEvent,
            'OrderStatusUpdatedEvent': OrderStatusUpdatedEvent,
            'OrderCancelledEvent': OrderCancelledEvent
        }
        
        event_class = event_classes.get(event_type)
        if not event_class:
            raise ValueError(f"Unknown event type: {event_type}")
        
        # Reconstruct event (simplified)
        if event_type == 'OrderCreatedEvent':
            return OrderCreatedEvent(
                event_data['order_id'],
                event_data['customer_id'],
                event_data['items'],
                event_data['total']
            )
        # Add other event types...
        
        return event_class(**event_data)

# Event Sourced Aggregate
class EventSourcedOrder(AggregateRoot):
    def __init__(self, order_id: str):
        super().__init__(order_id)
        self.customer_id: Optional[str] = None
        self.items: List[OrderItem] = []
        self.status = OrderStatus.PENDING
        self.total = 0.0
        self.created_at: Optional[float] = None
    
    def create_order(self, customer_id: str, items: List[OrderItem]):
        """Create new order"""
        if self.customer_id is not None:
            raise ValueError("Order already exists")
        
        total = sum(item.quantity * item.price for item in items)
        event = OrderCreatedEvent(self.aggregate_id, customer_id, items, total)
        self.apply_event(event)
    
    def update_status(self, new_status: OrderStatus):
        """Update order status"""
        if self.status == new_status:
            return
        
        old_status = self.status
        event = OrderStatusUpdatedEvent(self.aggregate_id, old_status, new_status)
        self.apply_event(event)
    
    def cancel_order(self, reason: str):
        """Cancel order"""
        if self.status == OrderStatus.CANCELLED:
            return
        
        event = OrderCancelledEvent(self.aggregate_id, reason)
        self.apply_event(event)
    
    # Event application methods
    def _apply_OrderCreatedEvent(self, event: OrderCreatedEvent):
        self.customer_id = event.customer_id
        self.items = event.items
        self.total = event.total
        self.status = OrderStatus.PENDING
        self.created_at = event.timestamp
    
    def _apply_OrderStatusUpdatedEvent(self, event: OrderStatusUpdatedEvent):
        self.status = event.new_status
    
    def _apply_OrderCancelledEvent(self, event: OrderCancelledEvent):
        self.status = OrderStatus.CANCELLED

# Repository with Event Sourcing
class EventSourcedRepository:
    def __init__(self, event_store: EventStore):
        self.event_store = event_store
    
    def get_by_id(self, aggregate_id: str, aggregate_class: type) -> Optional[AggregateRoot]:
        """Load aggregate from event store"""
        events = self.event_store.get_events(aggregate_id)
        
        if not events:
            return None
        
        # Create aggregate and load from history
        aggregate = aggregate_class(aggregate_id)
        aggregate.load_from_history(events)
        
        return aggregate
    
    def save(self, aggregate: AggregateRoot):
        """Save aggregate events to event store"""
        uncommitted_events = aggregate.get_uncommitted_events()
        
        if not uncommitted_events:
            return
        
        # Save events with optimistic concurrency check
        expected_version = aggregate.version - len(uncommitted_events)
        
        try:
            self.event_store.append_events(
                aggregate.aggregate_id,
                uncommitted_events,
                expected_version
            )
            
            aggregate.mark_events_as_committed()
            
        except ConcurrencyError:
            # Handle concurrency conflict
            raise ConcurrencyError(
                f"Concurrency conflict when saving aggregate {aggregate.aggregate_id}"
            )

# Event Sourcing Usage Example
def event_sourcing_example():
    # Setup
    event_store = InMemoryEventStore()
    repository = EventSourcedRepository(event_store)
    
    # Create and save order
    order = EventSourcedOrder("order-123")
    items = [OrderItem("product-1", 2, 29.99)]
    order.create_order("customer-456", items)
    
    repository.save(order)
    print(f"Order created with {len(order.get_uncommitted_events())} events")
    
    # Load order from event store
    loaded_order = repository.get_by_id("order-123", EventSourcedOrder)
    print(f"Loaded order status: {loaded_order.status}")
    print(f"Loaded order total: $\{loaded_order.total\}")
    
    # Update order
    loaded_order.update_status(OrderStatus.CONFIRMED)
    repository.save(loaded_order)
    
    # Demonstrate event history
    events = event_store.get_events("order-123")
    print(f"\\nEvent History for order-123:")
    for event in events:
        print(f"  {event.version}: {event.event_type} at {event.timestamp}")
    
    # Time travel - reconstruct state at specific version
    past_order = EventSourcedOrder("order-123")
    past_events = [e for e in events if e.version <= 1]  # Only first event
    past_order.load_from_history(past_events)
    print(f"\\nOrder state after first event: {past_order.status}")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>CQRS + Event Sourcing Integration</h2>
          
          <h3 style={styles.heading}>Complete Integration Pattern</h3>
          <p style={styles.paragraph}>
            Combining CQRS with Event Sourcing creates a powerful architecture where commands generate 
            events that are stored in an event store, and projections create optimized read models 
            from these events.
          </p>
          <pre style={styles.codeBlock}>
{`CQRS + Event Sourcing Architecture:

┌─────────────┐    ┌─────────────┐    ┌─────────────┐    ┌─────────────┐
│   Command   │───▶│  Aggregate  │───▶│Event Store  │───▶│Event Handler│
│             │    │             │    │             │    │(Projection) │
└─────────────┘    └─────────────┘    └─────────────┘    └─────────────┘
                                                                 │
                                                                 ▼
┌─────────────┐    ┌─────────────┐                    ┌─────────────┐
│   Query     │◀───│ Read Model  │◀───────────────────│Read Model DB│
│             │    │             │                    │             │
└─────────────┘    └─────────────┘                    └─────────────┘

Flow:
1. Command arrives
2. Load aggregate from event store
3. Execute business logic
4. Generate events
5. Save events to event store
6. Publish events
7. Event handlers update read models
8. Queries read from optimized read models

Complete Implementation:

from typing import Dict, List, Any, Optional
import asyncio
import json

class EventBus:
    def __init__(self):
        self._handlers: Dict[str, List[Callable]] = {}
    
    def subscribe(self, event_type: str, handler: Callable):
        if event_type not in self._handlers:
            self._handlers[event_type] = []
        self._handlers[event_type].append(handler)
    
    async def publish(self, event: Event):
        event_type = type(event).__name__
        handlers = self._handlers.get(event_type, [])
        
        # Execute handlers concurrently
        tasks = [handler(event) for handler in handlers]
        if tasks:
            await asyncio.gather(*tasks, return_exceptions=True)

class CQRSEventSourcingSystem:
    def __init__(self):
        self.event_store = InMemoryEventStore()
        self.repository = EventSourcedRepository(self.event_store)
        self.event_bus = EventBus()
        self.read_model_stores = {}
        self.command_handlers = {}
        self.query_handlers = {}
        
        self._setup_projections()
    
    def _setup_projections(self):
        """Setup event handlers for read model projections"""
        # Order projection
        order_projection = OrderProjectionHandler(self.get_read_store('orders'))
        self.event_bus.subscribe('OrderCreatedEvent', order_projection.handle_order_created)
        self.event_bus.subscribe('OrderStatusUpdatedEvent', order_projection.handle_status_updated)
        self.event_bus.subscribe('OrderCancelledEvent', order_projection.handle_order_cancelled)
        
        # Customer summary projection
        customer_projection = CustomerSummaryProjection(self.get_read_store('customers'))
        self.event_bus.subscribe('OrderCreatedEvent', customer_projection.handle_order_created)
        self.event_bus.subscribe('OrderCancelledEvent', customer_projection.handle_order_cancelled)
    
    def get_read_store(self, store_name: str):
        if store_name not in self.read_model_stores:
            self.read_model_stores[store_name] = InMemoryReadModelStore()
        return self.read_model_stores[store_name]
    
    async def execute_command(self, command: Command) -> Dict[str, Any]:
        """Execute command and publish resulting events"""
        try:
            # Get aggregate
            if hasattr(command, 'order_id'):
                aggregate = self.repository.get_by_id(command.order_id, EventSourcedOrder)
                if not aggregate:
                    raise ValueError(f"Order {command.order_id} not found")
            else:
                # New aggregate
                aggregate = EventSourcedOrder(str(uuid.uuid4()))
            
            # Execute command
            if isinstance(command, CreateOrderCommand):
                aggregate.create_order(command.customer_id, command.items)
            elif isinstance(command, UpdateOrderStatusCommand):
                aggregate.update_status(command.status)
            elif isinstance(command, CancelOrderCommand):
                aggregate.cancel_order(command.reason)
            
            # Save aggregate (this saves events to event store)
            self.repository.save(aggregate)
            
            # Publish events to event bus
            events = aggregate.get_uncommitted_events()
            for event in events:
                await self.event_bus.publish(event)
            
            return {
                'success': True,
                'aggregate_id': aggregate.aggregate_id,
                'version': aggregate.version,
                'events_count': len(events)
            }
            
        except Exception as e:
            return {
                'success': False,
                'error': str(e)
            }
    
    def execute_query(self, query_type: str, **kwargs) -> Any:
        """Execute query against read models"""
        if query_type == 'get_order':
            store = self.get_read_store('orders')
            return store.get_order(kwargs['order_id'])
        
        elif query_type == 'get_customer_orders':
            store = self.get_read_store('orders')
            return store.get_orders_by_customer(kwargs['customer_id'])
        
        elif query_type == 'get_customer_summary':
            store = self.get_read_store('customers')
            return store.get_customer_summary(kwargs['customer_id'])
        
        else:
            raise ValueError(f"Unknown query type: {query_type}")

# Enhanced Projections
class OrderProjectionHandler:
    def __init__(self, read_store):
        self.read_store = read_store
    
    async def handle_order_created(self, event: OrderCreatedEvent):
        """Project OrderCreatedEvent to read model"""
        read_model = {
            'order_id': event.order_id,
            'customer_id': event.customer_id,
            'status': OrderStatus.PENDING.value,
            'total': event.total,
            'items_count': len(event.items),
            'created_at': event.timestamp,
            'updated_at': event.timestamp
        }
        
        await self.read_store.save_order(read_model)
    
    async def handle_status_updated(self, event: OrderStatusUpdatedEvent):
        """Project OrderStatusUpdatedEvent to read model"""
        order = await self.read_store.get_order(event.order_id)
        if order:
            order['status'] = event.new_status.value
            order['updated_at'] = event.timestamp
            await self.read_store.save_order(order)
    
    async def handle_order_cancelled(self, event: OrderCancelledEvent):
        """Project OrderCancelledEvent to read model"""
        order = await self.read_store.get_order(event.order_id)
        if order:
            order['status'] = OrderStatus.CANCELLED.value
            order['updated_at'] = event.timestamp
            order['cancellation_reason'] = event.reason
            await self.read_store.save_order(order)

class CustomerSummaryProjection:
    def __init__(self, read_store):
        self.read_store = read_store
    
    async def handle_order_created(self, event: OrderCreatedEvent):
        """Update customer summary when order is created"""
        summary = await self.read_store.get_customer_summary(event.customer_id)
        
        if not summary:
            summary = {
                'customer_id': event.customer_id,
                'total_orders': 0,
                'total_spent': 0.0,
                'last_order_date': 0,
                'cancelled_orders': 0
            }
        
        summary['total_orders'] += 1
        summary['total_spent'] += event.total
        summary['last_order_date'] = max(summary['last_order_date'], event.timestamp)
        
        await self.read_store.save_customer_summary(summary)
    
    async def handle_order_cancelled(self, event: OrderCancelledEvent):
        """Update customer summary when order is cancelled"""
        summary = await self.read_store.get_customer_summary(event.customer_id)
        if summary:
            summary['cancelled_orders'] += 1
            await self.read_store.save_customer_summary(summary)

# Async Read Model Store
class InMemoryReadModelStore:
    def __init__(self):
        self._orders = {}
        self._customer_summaries = {}
    
    async def save_order(self, order_data: Dict[str, Any]):
        self._orders[order_data['order_id']] = order_data
    
    async def get_order(self, order_id: str) -> Optional[Dict[str, Any]]:
        return self._orders.get(order_id)
    
    async def get_orders_by_customer(self, customer_id: str) -> List[Dict[str, Any]]:
        return [order for order in self._orders.values() 
                if order['customer_id'] == customer_id]
    
    async def save_customer_summary(self, summary: Dict[str, Any]):
        self._customer_summaries[summary['customer_id']] = summary
    
    async def get_customer_summary(self, customer_id: str) -> Optional[Dict[str, Any]]:
        return self._customer_summaries.get(customer_id)

# Usage Example
async def complete_example():
    system = CQRSEventSourcingSystem()
    
    # Create order
    items = [OrderItem("product-1", 2, 29.99), OrderItem("product-2", 1, 49.99)]
    create_command = CreateOrderCommand("customer-123", items)
    
    result = await system.execute_command(create_command)
    print(f"Command result: {result}")
    
    order_id = result['aggregate_id']
    
    # Wait a bit for projections to complete
    await asyncio.sleep(0.1)
    
    # Query read models
    order = system.execute_query('get_order', order_id=order_id)
    print(f"Order from read model: {order}")
    
    customer_summary = system.execute_query('get_customer_summary', customer_id="customer-123")
    print(f"Customer summary: {customer_summary}")
    
    # Update order status
    update_command = UpdateOrderStatusCommand(order_id, OrderStatus.CONFIRMED)
    await system.execute_command(update_command)
    
    # Wait for projection
    await asyncio.sleep(0.1)
    
    # Query updated state
    updated_order = system.execute_query('get_order', order_id=order_id)
    print(f"Updated order: {updated_order}")

# Run the example
# asyncio.run(complete_example())`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Start simple and evolve:</strong> Begin with traditional architecture and 
              introduce CQRS/Event Sourcing only when complexity justifies the overhead.
            </li>
            <li style={styles.listItem}>
              <strong>Design events carefully:</strong> Events are contracts that should be 
              immutable and backward compatible. Plan for event schema evolution from the start.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper error handling:</strong> Handle projection failures, 
              concurrency conflicts, and event store outages gracefully.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor and observe:</strong> Implement comprehensive monitoring for 
              command processing times, projection lag, and event store performance.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for snapshots:</strong> For aggregates with long event histories, 
              implement snapshotting to optimize performance.
            </li>
            <li style={styles.listItem}>
              <strong>Test thoroughly:</strong> Test event replay, projection rebuilding, and 
              failure scenarios to ensure system reliability.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            CQRS and Event Sourcing are powerful architectural patterns that enable highly scalable, 
            maintainable, and auditable systems. CQRS optimizes read and write operations independently, 
            while Event Sourcing provides complete audit trails and temporal query capabilities.
          </p>
          <p style={styles.paragraph}>
            These patterns are most valuable in complex domains with intricate business logic, high 
            scalability requirements, and strong audit needs. When implemented correctly, they provide 
            significant benefits, but they also introduce complexity that must be carefully managed 
            through proper design, testing, and operational practices.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CQRSEventSourcing;
